import { all, call, put, takeEvery, takeLeading } from 'typed-redux-saga'
import { Action, Actions } from '~/actions'
import { loadTranslations } from '~/components/Locale'
import { ProductsDocument, UpsellCheckoutCreateDocument, UpsellDocument } from '~/gql'
import type { CartCreateMutation, GQLResponse, UpsellQuery } from '~/types'
import { AukError, extractUpsells, getCurrentLanguage, isError, isOk, shopifyFetch } from '~/utils'

export function* productSagas() {
	yield* takeLeading('PRODUCTS_LOAD', loadProducts)
	yield* takeEvery('UPSELL_LOAD', loadUpsells)
}

function* loadProducts(action: Action<'PRODUCTS_LOAD'>) {
	let error: AukError | undefined
	try {
		const res = yield* call(() => shopifyFetch(ProductsDocument, {}))
		if (isOk(res)) {
			return yield* put(Actions.productsLoaded(res.data))
		} else if (isError(res)) {
			error = res.errors[0]
		}
	} catch (e) {
		const lang = getCurrentLanguage().locale
		const errorMessage = yield* call(loadTranslations, lang, 'react.products.load_error')
		error = new AukError(errorMessage, error, { action: { ...action } })
	}
	if (error) yield* put(Actions.productsFailed(error))
}

function* loadUpsells(action: Action<'UPSELL_LOAD'>) {
	const product = action.payload
	const variantIds = extractUpsells(product, action.meta).map(u => u.id)
	const lines = [product.variants[0].id, ...variantIds].map(merchandiseId => ({ merchandiseId, quantity: 1 }))

	try {
		const [_cart, _upsells] = yield* all([
			call(() => shopifyFetch(UpsellCheckoutCreateDocument, { lines })),
			call(() => shopifyFetch(UpsellDocument, { variantIds }))
		])
		const cart = _cart as GQLResponse<CartCreateMutation>
		const upsells = _upsells as GQLResponse<UpsellQuery>

		if (isOk(upsells)) yield* put(Actions.upsellsLoaded(upsells.data, product.id))
		else yield* put(Actions.upsellsLoaded([], product.id))
		if (isOk(cart)) yield* put(Actions.upsellsCartLoaded(cart.data, product.id))
	} catch {
		yield* put(Actions.upsellsLoaded([], product.id))
	}
}
